/**
 * PublicApplyPage — `/jobs/:slug/apply` — the actual apply form the
 * Passport-First Apply flow (Iter 101) routes candidates to. Posts to the
 * existing `POST /api/job-applications` endpoint so results land in
 * `job_applications` and immediately show up in the admin drawer.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft, CheckCircle2, IdCard, Upload, FileText, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

const RESUME_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const RESUME_MAX_MB = 5;
const RESUME_MAX_BYTES = RESUME_MAX_MB * 1024 * 1024;

export default function PublicApplyPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [job, setJob] = useState(null);
    const [state, setState] = useState("loading");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [resumeFile, setResumeFile] = useState(null);
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
        cover_letter: "",
    });

    useEffect(() => {
        api.get(`/public/jobs/by-slug/${slug}`)
            .then((r) => { setJob(r.data); setState("ok"); })
            .catch((err) => setState(err?.response?.status === 404 ? "notfound" : "error"));
    }, [slug]);

    useEffect(() => {
        if (user) setForm((prev) => ({
            ...prev,
            name: prev.name || user.name || "",
            email: prev.email || user.email || "",
        }));
    }, [user]);

    const onPickResume = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const name = (f.name || "").toLowerCase();
        const ok = /\.(pdf|doc|docx)$/.test(name);
        if (!ok) {
            toast.error("Only PDF, DOC, or DOCX resumes are supported.");
            e.target.value = "";
            return;
        }
        if (f.size > RESUME_MAX_BYTES) {
            toast.error(`Resume is too large. Max ${RESUME_MAX_MB} MB.`);
            e.target.value = "";
            return;
        }
        setResumeFile(f);
    };

    const clearResume = () => {
        setResumeFile(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!job) return;
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        try {
            setSubmitting(true);
            const fd = new FormData();
            fd.append("job_id", job.id);
            fd.append("name", form.name);
            fd.append("email", form.email);
            fd.append("phone", form.phone || "");
            fd.append("cover_letter", form.cover_letter || "");
            if (resumeFile) fd.append("resume", resumeFile);
            await api.post("/job-applications", fd);
            trackEvent("job_application_completed", {
                job_id: job.id,
                job_slug: job.slug || slug,
                authenticated: !!user,
                user_type: user?.role || "guest",
            });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (state === "loading") {
        return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
    }
    if (state === "notfound" || !job) {
        return (
            <div className="container-app py-16 text-center" data-testid="apply-notfound">
                <h1 className="text-2xl font-heading">Job not found.</h1>
                <Link to="/jobs" className="text-emerald-600 hover:underline mt-3 inline-block">← Browse open roles</Link>
            </div>
        );
    }
    if (job.is_expired) {
        return (
            <div className="container-app py-16 text-center" data-testid="apply-expired">
                <h1 className="text-2xl font-heading">This role is no longer accepting applications.</h1>
                <Link to="/jobs" className="text-emerald-600 hover:underline mt-3 inline-block">← Browse open roles</Link>
            </div>
        );
    }

    return (
        <div className="container-app max-w-2xl py-10" data-testid="public-apply-page">
            <Helmet><title>{`Apply to ${job.title} · Simonara`}</title></Helmet>
            <Link to={`/careers/${slug}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
                <ArrowLeft size={12} /> Back to job
            </Link>
            {job.req_number && (
                <div className="text-[10px] uppercase tracking-wider font-mono font-semibold text-muted-foreground mb-1">
                    Req · {job.req_number}
                </div>
            )}
            <h1 className="text-3xl font-heading font-bold">{job.title}</h1>
            <p className="text-muted-foreground mt-1">{[job.company, job.location].filter(Boolean).join(" · ")}</p>

            {done ? (
                <div className="mt-8 card-soft p-8 text-center" data-testid="apply-success">
                    <CheckCircle2 size={28} className="mx-auto text-emerald-500" />
                    <h2 className="mt-2 text-2xl font-heading font-semibold">Application submitted.</h2>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                        We've received your application for <span className="font-medium text-foreground">{job.title}</span>. The hiring team will reach out via email if there's a fit.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        {!user && (
                            <Link to="/register?intent=passport" className="btn-green inline-flex items-center gap-2" data-testid="apply-success-passport-cta">
                                <IdCard size={14} /> Build a Talent Passport™
                            </Link>
                        )}
                        <Link to="/jobs" className="btn-outline">Browse more jobs</Link>
                    </div>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="public-apply-form">
                    <div>
                        <label className="text-sm font-medium">Full name *</label>
                        <input
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background focus:outline-none focus:border-emerald-500"
                            data-testid="apply-name"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Email *</label>
                        <input
                            required
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background focus:outline-none focus:border-emerald-500"
                            data-testid="apply-email"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Phone <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background focus:outline-none focus:border-emerald-500"
                            data-testid="apply-phone"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Why are you a fit? <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <textarea
                            rows={5}
                            value={form.cover_letter}
                            onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                            placeholder="A short note about what draws you to this role and how your background lines up."
                            className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background focus:outline-none focus:border-emerald-500"
                            data-testid="apply-cover"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Resume <span className="text-muted-foreground font-normal">(optional · PDF, DOC, DOCX · max {RESUME_MAX_MB} MB)</span></label>
                        {resumeFile ? (
                            <div
                                className="mt-1 flex items-center justify-between gap-3 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/5"
                                data-testid="apply-resume-selected"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <FileText size={16} className="text-emerald-600 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{resumeFile.name}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {(resumeFile.size / 1024).toFixed(0)} KB
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={clearResume}
                                    className="text-muted-foreground hover:text-foreground p-1 rounded"
                                    data-testid="apply-resume-clear"
                                    aria-label="Remove selected resume"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <label
                                htmlFor="apply-resume-input"
                                className="mt-1 flex items-center gap-2 px-3 py-3 rounded-md border border-dashed border-border bg-background hover:border-emerald-500/60 hover:bg-emerald-500/5 cursor-pointer transition"
                                data-testid="apply-resume-dropzone"
                            >
                                <Upload size={16} className="text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                    <span className="font-medium text-foreground">Click to upload</span>
                                    {" "}your resume — PDF, DOC, or DOCX up to {RESUME_MAX_MB} MB.
                                </span>
                            </label>
                        )}
                        <input
                            ref={fileRef}
                            id="apply-resume-input"
                            type="file"
                            accept={RESUME_ACCEPT}
                            onChange={onPickResume}
                            className="hidden"
                            data-testid="apply-resume-input"
                        />
                    </div>
                    {!user && (
                        <p className="text-xs text-muted-foreground italic">
                            Applying without a Talent Passport™? You can still submit here — but a Passport gets you into every future match automatically.
                        </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-green inline-flex items-center gap-2 disabled:opacity-60"
                            data-testid="apply-submit"
                        >
                            {submitting && <Loader2 size={14} className="animate-spin" />}
                            Submit application
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/careers/${slug}`)}
                            className="text-sm text-muted-foreground hover:text-foreground"
                            data-testid="apply-cancel"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
